/**
 * The two inputs every accounting form is built from, styled exactly like
 * the rest of the app's forms (AddDialog's fieldStyle): a labelled text
 * field, and a searchable picker. Labels always sit on the border, so a
 * filled field and an empty one line up.
 *
 * The picker is an Autocomplete rather than a plain select - the same
 * choice the order and container forms made - and it says so when it has
 * nothing to offer instead of opening an empty menu.
 */

import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

import SuiBox from "components/SuiBox";
import { fieldStyle } from "components/AddDialog";

export const FIELD_SX = fieldStyle(null);

export function Field({ label, helperText, InputLabelProps, sx, ...rest }) {
  return (
    <TextField
      label={label}
      fullWidth
      helperText={helperText}
      InputLabelProps={{ shrink: true, ...InputLabelProps }}
      sx={sx || FIELD_SX}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  );
}

Field.defaultProps = { helperText: " ", InputLabelProps: {}, sx: null };
Field.propTypes = {
  label: PropTypes.node.isRequired,
  helperText: PropTypes.node,
  // eslint-disable-next-line react/forbid-prop-types
  InputLabelProps: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  sx: PropTypes.any,
};

/**
 * Pick one of `options` ({id, ...}). `value` and `onChange` deal in the id,
 * which is what the forms send to the server.
 */
export function Pick({
  label,
  options,
  value,
  onChange,
  getLabel,
  getHint,
  getDepth,
  isDisabled,
  noOptionsText,
  helperText,
  placeholder,
}) {
  const picked = options.find((o) => String(o.id) === String(value)) || null;

  return (
    <Autocomplete
      options={options}
      value={picked}
      autoHighlight
      openOnFocus
      fullWidth
      getOptionLabel={getLabel}
      getOptionDisabled={isDisabled || undefined}
      isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
      noOptionsText={noOptionsText}
      onChange={(_, o) => onChange(o ? o.id : "")}
      renderOption={(props, o) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <li {...props} key={o.id}>
          <SuiBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap={2}
            // a tree's options step in by how deep they sit
            sx={getDepth ? { paddingInlineStart: getDepth(o) * 2 } : undefined}
          >
            <span>{getLabel(o)}</span>
            {getHint && <span style={{ fontSize: 12, opacity: 0.6 }}>{getHint(o)}</span>}
          </SuiBox>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          sx={FIELD_SX}
        />
      )}
    />
  );
}

Pick.defaultProps = {
  value: "",
  getHint: null,
  getDepth: null,
  isDisabled: null,
  noOptionsText: "Nothing to choose from",
  helperText: " ",
  placeholder: "Choose...",
};

Pick.propTypes = {
  label: PropTypes.node.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  getLabel: PropTypes.func.isRequired,
  getHint: PropTypes.func,
  getDepth: PropTypes.func,
  isDisabled: PropTypes.func,
  noOptionsText: PropTypes.node,
  helperText: PropTypes.node,
  placeholder: PropTypes.string,
};

/** A small heading that splits a form into the questions it asks. */
export function Section({ title, children }) {
  return (
    <SuiBox mb={1}>
      <SuiBox
        component="p"
        sx={({ palette, functions: { rgba } }) => ({
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: rgba(palette.dark.main, 0.55),
          m: 0,
          mb: 1.25,
        })}
      >
        {title}
      </SuiBox>
      {children}
    </SuiBox>
  );
}

Section.propTypes = { title: PropTypes.node.isRequired, children: PropTypes.node.isRequired };

/** Option lists for the fixed choices. */
export const asOptions = (values) => values.map((v) => ({ id: v, name: v }));
